// src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import botablewhite from '../../images/botable-white.png';
import botableLogo from '../../images/Botable-Logo.png';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&display=swap');

  @keyframes header-in {
    from { opacity:0; transform: translateY(-100%); }
    to   { opacity:1; transform: translateY(0); }
  }
  .header-animate { animation: header-in .45s cubic-bezier(.22,.68,0,1.1) both; }

  .logo-wrap { transition: transform .25s cubic-bezier(.34,1.56,.64,1), filter .25s ease; }
  .logo-wrap:hover { transform: scale(1.06); filter: drop-shadow(0 0 10px rgba(108,99,255,.45)); }

  .hdr-link { position:relative; transition:color .2s ease; white-space:nowrap; }
  .hdr-link::after {
    content:''; position:absolute; bottom:-3px; left:50%; right:50%;
    height:2px; border-radius:99px;
    background:linear-gradient(90deg,#6c63ff,#a78bfa);
    transition:left .25s ease, right .25s ease;
  }
  .hdr-link:hover::after { left:0; right:0; }

  /* scrollspy pills */
  .spy-pill {
    position:relative; padding:5px 14px; border-radius:99px;
    font-size:12.5px; font-weight:600; cursor:pointer; border:none;
    background:transparent; white-space:nowrap;
    font-family:'Sora',sans-serif;
    transition:color .2s ease, background .25s ease, box-shadow .25s ease, transform .18s ease;
  }
  .spy-pill:hover { transform:translateY(-1px); }
  .spy-pill.act-l {
    background:linear-gradient(135deg,rgba(108,99,255,.18),rgba(139,92,246,.12));
    color:#5b52ee;
    box-shadow:0 0 0 1.5px rgba(108,99,255,.3), 0 4px 12px rgba(108,99,255,.18);
  }
  .spy-pill.act-d {
    background:linear-gradient(135deg,rgba(108,99,255,.28),rgba(139,92,246,.2));
    color:#c4b5fd;
    box-shadow:0 0 0 1.5px rgba(167,139,250,.4), 0 4px 12px rgba(108,99,255,.25);
  }
  .spy-pill.inact-l { color:#6b7280; }
  .spy-pill.inact-l:hover { color:#5b52ee; background:rgba(108,99,255,.08); }
  .spy-pill.inact-d { color:#9ca3af; }
  .spy-pill.inact-d:hover { color:#c4b5fd; background:rgba(108,99,255,.14); }

  /* toggle */
  .toggle-track {
    position:relative; width:44px; height:24px; border-radius:99px;
    cursor:pointer; transition:background .3s ease; flex-shrink:0;
  }
  .toggle-thumb {
    position:absolute; top:3px; left:3px; width:18px; height:18px; border-radius:50%;
    background:#fff; transition:transform .3s cubic-bezier(.34,1.56,.64,1);
    display:flex; align-items:center; justify-content:center;
    font-size:10px; line-height:1; box-shadow:0 1px 4px rgba(0,0,0,.25);
  }
  .toggle-thumb.on { transform:translateX(20px); }

  .header-glass {
    backdrop-filter:blur(20px) saturate(200%);
    -webkit-backdrop-filter:blur(20px) saturate(200%);
  }

  @keyframes menu-drop { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
  .menu-drop { animation:menu-drop .2s ease both; }

  /* scroll progress */
  .hdr-progress {
    position:absolute; bottom:0; left:0; height:2px;
    background:linear-gradient(90deg,#6c63ff,#a78bfa,#38bdf8);
    transition:width .08s linear; border-radius:0 2px 2px 0;
    pointer-events:none;
  }

  /* Sign In ghost button */
  .hdr-signin {
    display:inline-flex; align-items:center; gap:5px;
    padding:7px 16px; border-radius:11px;
    font-size:13px; font-weight:700;
    font-family:'Sora',sans-serif; cursor:pointer; white-space:nowrap;
    transition:all .2s ease; text-decoration:none;
  }
  .hdr-signin-l {
    border:1.5px solid rgba(108,99,255,.4); color:#5b52ee;
    background:rgba(108,99,255,.07);
  }
  .hdr-signin-l:hover {
    background:rgba(108,99,255,.14); border-color:rgba(108,99,255,.65);
    transform:scale(1.04); box-shadow:0 4px 14px rgba(108,99,255,.2);
  }
  .hdr-signin-d {
    border:1.5px solid rgba(167,139,250,.35); color:#c4b5fd;
    background:rgba(108,99,255,.12);
  }
  .hdr-signin-d:hover {
    background:rgba(108,99,255,.22); border-color:rgba(167,139,250,.6);
    transform:scale(1.04); box-shadow:0 4px 14px rgba(108,99,255,.28);
  }
`;

const HOME_SECTIONS = [
  { id:'hero',     label:'Home'     },
  { id:'products', label:'Products' },
  { id:'plans',    label:'Plans'    },
  { id:'agencies', label:'Agencies' },
  { id:'security', label:'Security' },
  { id:'about',    label:'About'    },
  { id:'support',  label:'Support'  },
];

const Header = ({ isDarkMode, toggleDarkMode, activeSection = 'hero' }) => {
  const [scrolled,   setScrolled]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrollPct,  setScrollPct]  = useState(0);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const dk = isDarkMode;

  useEffect(() => {
    const fn = () => {
      setScrolled(window.scrollY > 10);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollPct(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    window.addEventListener('scroll', fn, { passive:true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = id => {
    document.getElementById(id)?.scrollIntoView({ behavior:'smooth', block:'start' });
    setMobileOpen(false);
  };

  const headerBg = scrolled
    ? dk
      ? 'bg-[#07071a]/93 border-b border-white/8 shadow-[0_4px_32px_rgba(0,0,0,.5)]'
      : 'bg-white/88 border-b border-gray-200/70 shadow-[0_4px_24px_rgba(108,99,255,.1)]'
    : dk
      ? 'bg-[#07071a]/60 border-b border-white/6'
      : 'bg-white/55 border-b border-white/70';

  return (
    <>
      <style>{STYLES}</style>

      <header className={`header-animate header-glass fixed top-0 left-0 w-full z-[100] transition-all duration-300 ${headerBg}`}>

        {/* Violet accent strip */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-violet-500 via-indigo-400 to-blue-400 z-10" />

        {/* Scroll progress bar */}
        {isHome && <div className="hdr-progress" style={{ width:`${scrollPct}%` }} />}

        {/* ── ROW 1: Logo ←→ Toggle + Sign In + Get Started ── */}
        <div className="flex items-center justify-between h-[62px] px-6 md:px-10 max-w-screen-2xl mx-auto">

          {/* Logo */}
          <Link to="/" className="logo-wrap flex-shrink-0">
            <img src={dk ? botablewhite : botableLogo} alt="Botable Logo" className="h-9 w-auto" />
          </Link>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-3">

            {/* Non-home: plain nav links */}
            {!isHome && (
              <>
                {[{to:'/',label:'Home'}].map(({to,label})=>(
                  <Link key={to} to={to}
                    className={`hdr-link text-sm font-semibold pb-0.5 ${dk?'text-gray-300 hover:text-white':'text-gray-600 hover:text-gray-900'}`}
                    style={{fontFamily:'Sora,sans-serif'}}>{label}</Link>
                ))}
                <div className={`h-5 w-px mx-1 ${dk?'bg-white/10':'bg-gray-200'}`} />
              </>
            )}

            {/* Dark mode toggle */}
            {toggleDarkMode && (
              <button onClick={toggleDarkMode} aria-label="Toggle dark mode"
                className={`toggle-track border ${dk?'bg-violet-600/80 border-violet-500/50':'bg-gray-200 border-gray-300'}`}>
                <span className={`toggle-thumb ${dk?'on':''}`}>{dk?'🌙':'☀️'}</span>
              </button>
            )}

            {/* Sign In ghost button — always in top row */}
            <Link to="/signin" className={`hdr-signin ${dk?'hdr-signin-d':'hdr-signin-l'}`}>
              🔐 Sign In
            </Link>

            {/* Get Started primary CTA */}
            <Link to="/signup"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white text-sm font-bold transition-all duration-200 hover:scale-[1.05] active:scale-95 shadow-md hover:shadow-violet-500/30"
              style={{fontFamily:'Sora,sans-serif'}}>
              Get Started →
            </Link>
          </div>

          {/* Mobile: toggle + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            {toggleDarkMode && (
              <button onClick={toggleDarkMode} aria-label="Toggle dark mode"
                className={`toggle-track border ${dk?'bg-violet-600/80 border-violet-500/50':'bg-gray-200 border-gray-300'}`}>
                <span className={`toggle-thumb ${dk?'on':''}`}>{dk?'🌙':'☀️'}</span>
              </button>
            )}
            <button onClick={()=>setMobileOpen(v=>!v)} aria-label="Open menu"
              className={`w-9 h-9 flex flex-col justify-center items-center gap-[5px] rounded-xl ${dk?'hover:bg-white/8':'hover:bg-gray-100'}`}>
              {[0,1,2].map(i=>(
                <span key={i} className={`block h-[2px] rounded-full transition-all duration-300 ${dk?'bg-gray-300':'bg-gray-600'} ${
                  mobileOpen&&i===0?'w-5 rotate-45 translate-y-[7px]'
                  :mobileOpen&&i===1?'w-0 opacity-0'
                  :mobileOpen&&i===2?'w-5 -rotate-45 -translate-y-[7px]'
                  :'w-5'
                }`}/>
              ))}
            </button>
          </div>
        </div>

        {/* ── ROW 2: Scrollspy pills — perfectly centered, no flanking elements ── */}
        {isHome && (
          <div className={`hidden md:block border-t ${dk?'border-white/6':'border-gray-100/80'}`}>
            <div className="relative h-11">
              {/* Absolutely centered pill group */}
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <div className="flex items-center gap-0.5">
                  {HOME_SECTIONS.map(({id,label})=>{
                    const act = activeSection===id;
                    return (
                      <button
                        key={id}
                        onClick={()=>scrollTo(id)}
                        className={`spy-pill ${act?(dk?'act-d':'act-l'):(dk?'inact-d':'inact-l')}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className={`menu-drop md:hidden absolute top-full left-0 w-full border-t px-4 py-3 flex flex-col gap-1.5 ${
            dk?'bg-[#0f0f24]/97 border-white/8 backdrop-blur-xl':'bg-white/97 border-gray-200 backdrop-blur-xl shadow-xl'
          }`}>
            {isHome
              ? HOME_SECTIONS.map(({id,label})=>(
                  <button key={id} onClick={()=>scrollTo(id)}
                    className={`text-sm font-semibold py-2.5 px-4 rounded-xl text-left transition-colors ${
                      activeSection===id
                        ? dk?'bg-violet-900/40 text-violet-300':'bg-violet-50 text-violet-700'
                        : dk?'text-gray-300 hover:bg-white/8 hover:text-white':'text-gray-700 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                    style={{fontFamily:'Sora,sans-serif'}}>{label}</button>
                ))
              : [{to:'/',label:'🏠 Home'}].map(({to,label})=>(
                  <Link key={to} to={to} onClick={()=>setMobileOpen(false)}
                    className={`text-sm font-semibold py-2.5 px-4 rounded-xl transition-colors ${dk?'text-gray-300 hover:bg-white/8 hover:text-white':'text-gray-700 hover:bg-violet-50 hover:text-violet-700'}`}
                    style={{fontFamily:'Sora,sans-serif'}}>{label}</Link>
                ))
            }
            {/* Mobile action buttons */}
            <div className="flex gap-2 mt-1">
              <Link to="/signin" onClick={()=>setMobileOpen(false)}
                className={`flex-1 text-center py-2.5 px-4 rounded-xl border text-sm font-bold transition-colors ${dk?'border-violet-500/40 text-violet-300 hover:bg-violet-900/30':'border-violet-400 text-violet-600 hover:bg-violet-50'}`}
                style={{fontFamily:'Sora,sans-serif'}}>🔐 Sign In</Link>
              <Link to="/signup" onClick={()=>setMobileOpen(false)}
                className="flex-1 text-center py-2.5 px-4 rounded-xl bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] text-white text-sm font-bold"
                style={{fontFamily:'Sora,sans-serif'}}>Get Started →</Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;